import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Check } from "lucide-react";
import { PRICING } from "@/constants/testIds";
import { formatApiErrorDetail, useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  {
    id: "free",
    name: "Cruise",
    price: "0",
    unit: "/ forever",
    tag: "For the daily driver",
    features: [
      "1 vehicle · VIN decoding",
      "5 marketplace chat windows",
      "15 enquiries per chat, then sales handoff",
      "5 diagnostics per month",
      "Basic maintenance timeline",
    ],
    cta: "Start free",
    highlight: false,
  },
  {
    id: "pro",
    name: "Autonomous",
    price: "12",
    unit: "/ month",
    tag: "Most enthusiasts pick this",
    features: [
      "Up to 3 vehicles",
      "50 marketplace chat windows",
      "15 enquiries per chat, then sales handoff",
      "Unlimited diagnostics + vision inspections",
      "OEM part cost estimates & TSB feed",
      "Export mechanic-ready PDFs",
    ],
    cta: "Go autonomous",
    highlight: true,
  },
  {
    id: "fleet",
    name: "Fleet",
    price: "custom",
    unit: "· contact",
    tag: "10+ vehicles · shops · dealers",
    features: [
      "Unlimited VINs · API access",
      "100 marketplace chat windows",
      "15 enquiries per chat, then sales handoff",
      "Multi-shop dashboard & SSO",
      "Predictive downtime alerts",
      "Dedicated integration engineer",
    ],
    cta: "Talk to sales",
    highlight: false,
  },
];

export const Pricing = () => {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [busyId, setBusyId] = useState("");
  const [notice, setNotice] = useState("");
  const currentPlan = (user?.plan || "free").toLowerCase();

  const handleCta = async (tier) => {
    setNotice("");
    if (tier.id === "free") {
      navigate(user ? "/console/marketplace" : "/signup");
      return;
    }
    if (!user) {
      navigate("/signup");
      return;
    }
    if (tier.id === currentPlan) {
      setNotice(`${tier.name} is already your plan.`);
      return;
    }
    setBusyId(tier.id);
    try {
      const { data } = await axios.post(`${API}/billing/subscribe`, { plan: tier.id });
      await refresh();
      setNotice(data?.message || `${tier.name} is now active.`);
    } catch (err) {
      setNotice(formatApiErrorDetail(err.response?.data?.detail) || "Could not activate that plan.");
    } finally {
      setBusyId("");
    }
  };

  return (
    <section
      id="pricing"
      data-testid={PRICING.section}
      className="relative bg-void py-24 lg:py-40 border-t border-white/5"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="max-w-3xl mb-16 lg:mb-20">
          <span className="chip-brand">/ 06 · plans</span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tighter leading-[0.95]">
            Priced like software.<br />
            <span className="text-white/40">Not like a shop.</span>
          </h2>
        </div>

        {notice && (
          <p className="mb-8 font-mono-tech text-[11px] uppercase tracking-widest text-cyan-electric">
            {notice}
          </p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.id}
              data-testid={PRICING.card(t.id)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              className={`relative p-8 lg:p-10 flex flex-col ${
                t.highlight
                  ? "bg-obsidian border border-mclaren/50"
                  : "bg-graphite border border-white/10"
              }`}
            >
              {t.highlight && (
                <>
                  <div className="pointer-events-none absolute -inset-px border border-mclaren/30 blur-[2px]" />
                  <div className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-mclaren/15 blur-3xl" />
                </>
              )}

              <div className="relative">
                <div className="flex items-baseline justify-between mb-1">
                  <h3 className="font-display text-2xl font-semibold text-white tracking-tight">
                    {t.name}
                  </h3>
                  {t.highlight && (
                    <span className="font-mono-tech text-[10px] uppercase tracking-widest text-mclaren">
                      ◆ popular
                    </span>
                  )}
                </div>
                <p className="font-mono-tech text-[11px] uppercase tracking-widest text-white/40 mb-8">
                  {t.tag}
                  {currentPlan === t.id ? " · current plan" : ""}
                </p>

                <div className="mb-8 flex items-baseline gap-2">
                  {t.price !== "custom" ? (
                    <>
                      <span className="font-display text-6xl font-semibold text-white tracking-tighter">
                        ${t.price}
                      </span>
                      <span className="font-mono-tech text-xs uppercase tracking-widest text-white/40">
                        {t.unit}
                      </span>
                    </>
                  ) : (
                    <span className="font-display text-6xl font-semibold text-white tracking-tighter">
                      Talk
                    </span>
                  )}
                </div>

                <ul className="space-y-3 mb-10">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-white/70 font-light">
                      <Check className="h-4 w-4 mt-0.5 text-cyan-electric shrink-0" strokeWidth={2} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={PRICING.cta(t.id)}
                  type="button"
                  disabled={Boolean(busyId)}
                  onClick={() => handleCta(t)}
                  className={`w-full h-12 font-mono-tech text-[11px] uppercase tracking-[0.2em] transition-all mt-auto disabled:opacity-50 ${
                    t.highlight
                      ? "bg-mclaren text-void hover:bg-sunset"
                      : "border border-white/20 text-white hover:border-mclaren hover:text-mclaren"
                  }`}
                >
                  {busyId === t.id ? "Activating…" : currentPlan === t.id ? "Current plan" : t.cta}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
